import { CaretLeft, CaretRight } from "@phosphor-icons/react";

/**
 * Reusable pagination bar.
 * Props:
 *   page (1-based), pageSize, total, onPageChange
 *   testid (root data-testid prefix)
 * Renders: "Showing X–Y of Z records" and page-number strip with ellipsis.
 */
export default function Pagination({ page, pageSize, total, onPageChange, testid = "pagination" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 10)));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, total);

  const go = (p) => {
    if (p < 1 || p > totalPages || p === currentPage) return;
    onPageChange(p);
  };

  const pageNumbers = buildPageList(currentPage, totalPages);

  return (
    <div className="flex items-center justify-between gap-4 p-3 border-t border-zinc-200 bg-white flex-wrap"
      data-testid={testid}>
      <div className="text-xs text-zinc-600" data-testid={`${testid}-summary`}>
        {total === 0 ? (
          "No records"
        ) : (
          <>Showing <b className="tabular-nums">{start}</b>–<b className="tabular-nums">{end}</b> of <b className="tabular-nums">{total}</b> records</>
        )}
      </div>
      <div className="flex items-center gap-1 text-xs">
        <button
          type="button"
          onClick={() => go(currentPage - 1)}
          disabled={currentPage <= 1}
          className="inline-flex items-center gap-1 h-8 px-2 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-prev`}
        >
          <CaretLeft size={12} /> Previous
        </button>
        {pageNumbers.map((p, idx) =>
          p === "..." ? (
            <span key={`e-${idx}`} className="px-2 text-zinc-400" data-testid={`${testid}-ellipsis`}>…</span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => go(p)}
              className={`min-w-8 h-8 px-2 border tabular-nums ${
                p === currentPage
                  ? "border-zinc-900 bg-zinc-900 text-white font-medium"
                  : "border-zinc-300 hover:bg-zinc-50"
              }`}
              data-testid={`${testid}-page-${p}`}
              aria-current={p === currentPage ? "page" : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          type="button"
          onClick={() => go(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="inline-flex items-center gap-1 h-8 px-2 border border-zinc-300 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid={`${testid}-next`}
        >
          Next <CaretRight size={12} />
        </button>
      </div>
    </div>
  );
}

function buildPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set([1, 2, total - 1, total, current - 1, current, current + 1]);
  const sorted = Array.from(pages).filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("...");
    out.push(sorted[i]);
  }
  return out;
}
